import {openDb} from "../../../Services/BrowserDatabaseIDB";
import {ensureStoresExist} from "../../../Services/BrowserDatabaseFunctions";

export async function checkForAccountAutoLogin() {
    console.log('checkForAccountAutoLogin');
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Account", "readwrite");
            const store = tx.objectStore("Account");

            return store.getAll().then(existingAccount => {
                console.log('existingAccount : ', existingAccount);
                if (!existingAccount) {
                    console.error(`No Account found in IndexedDB.`);
                    return null;
                }

                return existingAccount[0];
            });
        })
        .catch(function (error) {
            console.error("An error occurred during updateAccountValues:", error);
            throw error;
        });
}

export async function addAccountValue(_account) {
    try {
        const db = await openDb('pa', 1, ensureStoresExist);

        // Single readwrite tx that does everything
        const tx = db.transaction('Account', 'readwrite');
        const store = tx.objectStore('Account');

        const existing = (await store.getAll())[0];

        if (!existing || existing.id === _account.id) {
            // Same account (or empty): upsert
            await store.put(_account);
        } else {
            // Different account: replace singleton
            await store.clear();
            await store.put(_account);
        }

        // Ensure the transaction has fully committed
        await tx.complete;
        console.log('Successfully completed conditional account update.');
        return true;
    } catch (err) {
        console.error('Failed to execute conditional account update:', err);
        return false;
    }
}

export async function updateAccountValues(_user_id, updated_account) {
    console.log("updateAccountValues :", updated_account);

    if (!_user_id) {
        console.error("updateAccountValues: 'id' is required.");
        return Promise.reject(new Error("'id' field is missing."));
    }

    // Shallow clone to avoid mutating the original
    const incomingFields = { ...updated_account };

    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Account", "readwrite");
            const store = tx.objectStore("Account");

            // Get the existing contact by its id (keyPath is "id")
            return store.get(_user_id).then(existingAccount => {
                if (!existingAccount) {
                    console.error(`Account with ID ${_user_id} not found in IndexedDB.`);
                    return false;
                }

                // Merge existing data with incoming fields (incoming wins)
                const updatedAccount = {
                    ...existingAccount,
                    ...incomingFields,
                };

                return store.put(updatedAccount).then(() => {
                    console.log(`Account with ID ${_user_id} updated successfully.`);
                    return true;
                });
            });
        })
        .catch(function (error) {
            console.error("An error occurred during updateAccountValues:", error);
            throw error;
        });
}

export async function updateAccountDocument(_user_id, updated_document){
    console.log('updateAccountDocument');
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Account", "readwrite");
            const store = tx.objectStore("Account");

            return store.getAll().then(existingAccount => {
                console.log('existingAccount : ', existingAccount);
                if (!existingAccount) {
                    console.error(`No Account found in IndexedDB.`);
                    return null;
                }

                // 1. Get the primary account record (assuming index 0)
                const accountRecord = existingAccount[0];

                // 2. Ensure documents array exists
                if (!accountRecord.documents) {
                    console.warn("No documents array found in the account record.");
                    return null;
                }

                // 🎯 3. Update the document in the array
                const docIndex = accountRecord.documents.findIndex(
                    d => String(d.id) === String(updated_document.id) || String(d.temp_id) === String(updated_document.temp_id)
                );

                if (docIndex !== -1) {
                    // Spread the old doc and overwrite with new data (to preserve any extra fields)
                    accountRecord.documents[docIndex] = {
                        ...accountRecord.documents[docIndex],
                        ...updated_document
                    };
                } else {
                    console.warn(`Document with ID ${updated_document.id} not found. Inserting new document...`);
                    accountRecord.documents.push(updated_document);
                }

                // 4. Update the record in the store
                return store.put(accountRecord).then(() => {
                    console.log(`Document ${updated_document.id} updated in IndexedDB successfully.`);
                    return true;
                });


            });
        })
        .catch(function (error) {
            console.error("An error occurred during updateAccountDocument:", error);
            throw error;
        });
}

export async function addAccountEssentialItem(_user_id, _essential_item){
    console.log('addAccountEssentialItem');
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Account", "readwrite");
            const store = tx.objectStore("Account");

            return store.get(_user_id).then(accountRecord => {
                if (!accountRecord) {
                    console.error(`No Account record found for ID: ${_user_id}`);
                    return null;
                }

                // 1. Initialize essentials array if missing
                if (!accountRecord.essentials) {
                    accountRecord.essentials = [];
                }

                // 2. Check for existence (Match by server ID or the temporary creation ID)
                const exists = accountRecord.essentials.some(e =>
                    e.id === _essential_item.id ||
                    (_essential_item.temp_id && e.temp_id === _essential_item.temp_id)
                );

                if (!exists) {
                    // Sanitize the item to ensure no proxy objects are saved
                    // ✅ DROP-IN: Only include the specific Essential Template fields
                    const newEssential = {
                        id: _essential_item.id,
                        name: _essential_item.name,
                        category: _essential_item.category,
                        sub_category: _essential_item.sub_category,
                        private: _essential_item.private,
                        notes: _essential_item.notes || "",
                        status: _essential_item.status
                    };

                    accountRecord.essentials.push(newEssential);

                    // 3. Update the record back to IndexedDB
                    return store.put(accountRecord).then(() => {
                        console.log(`Essential item ${newEssential.id} saved to local Account storage.`);
                        return true;
                    });
                } else {
                    console.log("Essential already exists in local DB, skipping write.");
                    return true;
                }
            });
        })
        .catch(function (error) {
            console.error("An error occurred during updateAccountDocument:", error);
            throw error;
        });
}

export async function deleteAccountDocument(documentID){
    console.log('deleteAccountDocument');
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Account", "readwrite");
            const store = tx.objectStore("Account");

            return store.getAll().then(existingAccount => {
                console.log('existingAccount : ', existingAccount);
                if (!existingAccount) {
                    console.error(`No Account found in IndexedDB.`);
                    return null;
                }

                // 1. Get the primary account record (assuming index 0)
                const accountRecord = existingAccount[0];

                // 2. Ensure documents array exists
                if (!accountRecord.documents) {
                    console.warn("No documents array found in the account record.");
                    return null;
                }

                // 3. Filter out the document to be deleted
                const originalLength = accountRecord.documents.length;
                accountRecord.documents = accountRecord.documents.filter(doc => doc.id !== documentID);

                // Check if anything was actually removed
                if (accountRecord.documents.length === originalLength) {
                    console.warn(`Document with ID ${documentID} was not found in IndexedDB.`);
                    return null;
                }

                // 4. Update the record in the store
                return store.put(accountRecord).then(() => {
                    console.log(`Document ${documentID} removed from IndexedDB successfully.`);
                    return true;
                });


            });
        })
        .catch(function (error) {
            console.error("An error occurred during updateAccountValues:", error);
            throw error;
        });
}